import { createContext, useContext, useEffect, useId } from 'react';

/**
 * Unsaved-changes warning for settings forms (DEV-133).
 *
 * The browser asks before the tab is closed or reloaded. Inside Settings, the
 * section menu asks before switching section (SettingsHubPage reads
 * `hasUnsaved`). The console uses the classic BrowserRouter, which cannot
 * intercept every in-app link, so the main sidebar does not ask.
 */

export interface UnsavedRegistry {
  readonly set: (owner: string, dirty: boolean) => void;
}

export const UnsavedChangesContext = createContext<UnsavedRegistry | null>(null);

export const LEAVE_WITH_UNSAVED = 'You have unsaved changes on this page. Leave without saving them?';

export function useUnsavedChanges(dirty: boolean): void {
  const owner = useId();
  const registry = useContext(UnsavedChangesContext);
  useEffect(() => {
    registry?.set(owner, dirty);
    return () => registry?.set(owner, false);
  }, [registry, owner, dirty]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      // Older browsers show the prompt only when a value is set.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
}
